import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Pencil, Plus, X } from 'lucide-react';
import { Badge, Button, Card, ErrorState, Field, LoadingState, SectionHeader, SelectField } from '@pixlide/ui';
import {
  useActivatePlan,
  useCreatePlan,
  useDeactivatePlan,
  usePlans,
  useSetPlanStripePriceId,
  useUpdatePlan,
} from '@/api/queries/platform-admin';
import type { components, paths } from '@/api/generated/schema';

type Plan = components['schemas']['PixlideApplicationFeaturesPlatformAdminPlanAdminResponse'];

type UpdatePlanValues = NonNullable<
  paths['/api/v1/admin/plans/{planId}']['put']['requestBody']
>['content']['application/json'];

const createPlanSchema = z.object({
  // `PlanCode` est une énumération entière côté API (voir le document OpenAPI) : le champ
  // est saisi comme un nombre brut, converti au bon type nominal uniquement à l'envoi
  // (`onSubmit`), pour ne pas complexifier le schéma de validation avec un cast qui ne sert
  // qu'à satisfaire le compilateur.
  code: z.coerce.number().int().min(0, 'Le code de l’offre est obligatoire.'),
  name: z.string().min(1, 'Le nom est obligatoire.'),
  monthlyPriceAmount: z.coerce.number().min(0, 'Le tarif doit être positif.'),
  monthlyPriceCurrency: z.string().min(1).max(3),
  includedScreens: z.coerce.number().int().min(0),
  includedCustomVideos: z.coerce.number().int().min(0),
  sortOrder: z.coerce.number().int().min(0),
});

const updatePlanSchema = z.object({
  monthlyPriceAmount: z.coerce.number().min(0, 'Le tarif doit être positif.'),
  monthlyPriceCurrency: z.string().min(1).max(3),
  description: z.string(),
  includesPremiumTemplates: z.enum(['true', 'false']),
  includesAiEnhancement: z.enum(['true', 'false']),
  // Une feature par ligne : plus simple à éditer qu'un tableau JSON dans un champ texte.
  features: z.string(),
  commitmentMonths: z.coerce.number().int().min(0),
});

/**
 * Catalogue des offres — v1 : liste complète (actives et inactives), activation/désactivation
 * en un clic, un formulaire de création minimal reprenant les champs obligatoires côté API,
 * et un formulaire d'édition par carte (prix, description, capacités, features, engagement)
 * via `useUpdatePlan`.
 */
export function PlansPage() {
  const plans = usePlans();
  const createPlan = useCreatePlan();
  const activatePlan = useActivatePlan();
  const deactivatePlan = useDeactivatePlan();
  const setStripePriceId = useSetPlanStripePriceId();
  const updatePlan = useUpdatePlan();
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Pas de générique explicite sur `useForm` : `createPlanSchema` mêle des champs `z.coerce`
  // (type d'ENTRÉE `unknown`, l'utilisateur tape dans un `<input type="number">` qui rend
  // toujours une chaîne) à des champs `z.string()` (entrée = sortie). Fixer le générique sur
  // le type de SORTIE (`CreatePlanInput`) désynchronise le type attendu par `zodResolver`
  // de celui produit par `register`, ce que `exactOptionalPropertyTypes` refuse de
  // concilier. Laisser `useForm` inférer les deux types depuis le resolver règle le
  // problème à la source.
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(createPlanSchema),
    defaultValues: {
      monthlyPriceCurrency: 'EUR',
      includedScreens: 1,
      includedCustomVideos: 0,
      sortOrder: 0,
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    await createPlan.mutateAsync({
      ...values,
      code: values.code as components['schemas']['PixlideDomainBillingEnumsPlanCode'],
    });
    reset();
    setShowCreateForm(false);
  });

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <SectionHeader
        title="Offres"
        description="Catalogue complet, offres actives et inactives."
        action={
          <Button
            variant={showCreateForm ? 'ghost' : 'primary'}
            size="sm"
            onClick={() => setShowCreateForm((current) => !current)}
          >
            {showCreateForm ? (
              <>
                <X aria-hidden className="size-4" />
                Annuler
              </>
            ) : (
              <>
                <Plus aria-hidden className="size-4" />
                Nouvelle offre
              </>
            )}
          </Button>
        }
      />

      {showCreateForm ? (
        <Card className="p-6">
          <form onSubmit={onSubmit} noValidate className="grid gap-4 sm:grid-cols-2">
            <Field label="Code (PlanCode)" type="number" error={errors.code?.message} {...register('code')} />
            <Field label="Nom" error={errors.name?.message} {...register('name')} />
            <Field
              label="Tarif mensuel"
              type="number"
              step="0.01"
              error={errors.monthlyPriceAmount?.message}
              {...register('monthlyPriceAmount')}
            />
            <Field
              label="Devise"
              error={errors.monthlyPriceCurrency?.message}
              {...register('monthlyPriceCurrency')}
            />
            <Field
              label="Écrans inclus"
              type="number"
              error={errors.includedScreens?.message}
              {...register('includedScreens')}
            />
            <Field
              label="Vidéos sur-mesure incluses"
              type="number"
              error={errors.includedCustomVideos?.message}
              {...register('includedCustomVideos')}
            />
            <Field
              label="Ordre de tri"
              type="number"
              error={errors.sortOrder?.message}
              {...register('sortOrder')}
            />

            <Button type="submit" isLoading={isSubmitting || createPlan.isPending} className="sm:col-span-2">
              Créer l'offre
            </Button>
          </form>
        </Card>
      ) : null}

      {plans.isPending ? <LoadingState label="Chargement des offres…" /> : null}

      {plans.isError ? (
        <ErrorState message="Impossible de charger les offres." onRetry={() => void plans.refetch()} />
      ) : null}

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {(plans.data ?? []).map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            onToggleActive={() => {
              if (plan.isActive) {
                deactivatePlan.mutate(plan.id);
              } else {
                activatePlan.mutate(plan.id);
              }
            }}
            isToggling={activatePlan.isPending || deactivatePlan.isPending}
            onSetStripePriceId={(stripePriceId) => setStripePriceId.mutate({ planId: plan.id, stripePriceId })}
            isSavingStripePriceId={setStripePriceId.isPending}
            onUpdate={(payload) => updatePlan.mutateAsync({ planId: plan.id, ...payload })}
            isSavingUpdate={updatePlan.isPending}
          />
        ))}
      </div>
    </div>
  );
}

function PlanCard({
  plan,
  onToggleActive,
  isToggling,
  onSetStripePriceId,
  isSavingStripePriceId,
  onUpdate,
  isSavingUpdate,
}: {
  plan: Plan;
  onToggleActive: () => void;
  isToggling: boolean;
  onSetStripePriceId: (stripePriceId: string | null) => void;
  isSavingStripePriceId: boolean;
  onUpdate: (payload: UpdatePlanValues) => Promise<unknown>;
  isSavingUpdate: boolean;
}) {
  const [stripePriceId, setStripePriceId] = useState(plan.stripePriceId ?? '');
  const [showEditForm, setShowEditForm] = useState(false);

  return (
    <Card className="flex flex-col gap-3 p-6">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-lg font-semibold">{plan.name}</h3>
        <div className="flex items-center gap-2">
          <Badge tone={plan.isActive ? 'success' : 'neutral'}>{plan.isActive ? 'Active' : 'Inactive'}</Badge>
          <Button
            variant="ghost"
            size="sm"
            aria-label="Modifier l'offre"
            onClick={() => setShowEditForm((current) => !current)}
          >
            <Pencil aria-hidden className="size-4" />
          </Button>
        </div>
      </div>

      <p className="text-2xl font-semibold tracking-tight">
        {plan.monthlyPriceAmount} {plan.monthlyPriceCurrency}
        <span className="text-sm font-normal text-ink-500"> / mois</span>
      </p>

      <p className="text-sm text-ink-500">
        {plan.includedScreens} écran{plan.includedScreens > 1 ? 's' : ''} · {plan.includedCustomVideos}{' '}
        vidéo{plan.includedCustomVideos > 1 ? 's' : ''} sur-mesure
      </p>

      {plan.description ? <p className="text-sm text-ink-500">{plan.description}</p> : null}

      {showEditForm ? (
        <EditPlanForm
          plan={plan}
          onSubmit={async (payload) => {
            await onUpdate(payload);
            setShowEditForm(false);
          }}
          isSaving={isSavingUpdate}
        />
      ) : null}

      <form
        className="flex flex-col gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          onSetStripePriceId(stripePriceId.trim().length > 0 ? stripePriceId.trim() : null);
        }}
      >
        <Field
          label="Identifiant du tarif Stripe (price_...)"
          placeholder="price_1AbCdEfGhIjKlMn"
          value={stripePriceId}
          onChange={(event) => setStripePriceId(event.target.value)}
        />

        <p className="text-xs text-ink-300">Requis pour pouvoir activer l'offre dans le catalogue public.</p>

        <Button type="submit" variant="secondary" size="sm" isLoading={isSavingStripePriceId}>
          Relier à Stripe
        </Button>
      </form>

      <Button
        variant={plan.isActive ? 'secondary' : 'primary'}
        size="sm"
        isLoading={isToggling}
        onClick={onToggleActive}
        className="mt-auto"
      >
        {plan.isActive ? 'Désactiver' : 'Activer'}
      </Button>
    </Card>
  );
}

/**
 * Formulaire d'édition d'une offre existante — reprend exactement les champs modifiables
 * côté API (`UpdatePlanCommand`) : le nom, les écrans/vidéos inclus et l'ordre de tri ne
 * changent qu'à la création, seuls le tarif, la description, les capacités, les features et
 * l'engagement se corrigent après coup.
 */
function EditPlanForm({
  plan,
  onSubmit,
  isSaving,
}: {
  plan: Plan;
  onSubmit: (payload: UpdatePlanValues) => Promise<void>;
  isSaving: boolean;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(updatePlanSchema),
    defaultValues: {
      monthlyPriceAmount: plan.monthlyPriceAmount,
      monthlyPriceCurrency: plan.monthlyPriceCurrency,
      description: plan.description ?? '',
      includesPremiumTemplates: plan.includesPremiumTemplates ? 'true' : 'false',
      includesAiEnhancement: plan.includesAiEnhancement ? 'true' : 'false',
      features: plan.features.join('\n'),
      commitmentMonths: plan.commitmentMonths,
    },
  });

  const submit = handleSubmit(async (values) => {
    await onSubmit({
      monthlyPriceAmount: values.monthlyPriceAmount,
      monthlyPriceCurrency: values.monthlyPriceCurrency,
      description: values.description.trim().length > 0 ? values.description.trim() : null,
      includesPremiumTemplates: values.includesPremiumTemplates === 'true',
      includesAiEnhancement: values.includesAiEnhancement === 'true',
      features: values.features
        .split('\n')
        .map((feature) => feature.trim())
        .filter((feature) => feature.length > 0),
      commitmentMonths: values.commitmentMonths,
    });
  });

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-3 rounded-xl bg-ink-50 p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label="Tarif mensuel"
          type="number"
          step="0.01"
          error={errors.monthlyPriceAmount?.message}
          {...register('monthlyPriceAmount')}
        />
        <Field
          label="Devise"
          error={errors.monthlyPriceCurrency?.message}
          {...register('monthlyPriceCurrency')}
        />
      </div>

      <Field label="Description" error={errors.description?.message} {...register('description')} />

      <div className="grid gap-3 sm:grid-cols-2">
        <SelectField label="Templates premium" {...register('includesPremiumTemplates')}>
          <option value="false">Non</option>
          <option value="true">Oui</option>
        </SelectField>
        <SelectField label="Amélioration IA" {...register('includesAiEnhancement')}>
          <option value="false">Non</option>
          <option value="true">Oui</option>
        </SelectField>
      </div>

      <Field
        label="Engagement (mois)"
        type="number"
        error={errors.commitmentMonths?.message}
        hint="0 = sans engagement, résiliable en fin de mois."
        {...register('commitmentMonths')}
      />

      <label className="flex flex-col gap-1.5">
        <span className="text-[11px] leading-4 font-medium text-ink-300">Features (une par ligne)</span>
        <textarea
          rows={4}
          className="rounded-xl bg-white px-3.5 py-2.5 text-sm text-ink-900 ring-1 ring-ink-100 transition focus-visible:outline-none focus-within:ring-2 focus-within:ring-ink-900"
          {...register('features')}
        />
      </label>

      <Button type="submit" isLoading={isSubmitting || isSaving} className="self-start">
        Enregistrer les modifications
      </Button>
    </form>
  );
}
